import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Copy, Archive, ArchiveRestore, Trash2, Settings, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  fetchAdminStats, fetchAdminLots, duplicateLot, archiveLot, deleteLot,
  fetchSettings, saveSettings, b2bBase,
} from "@/b2b/b2bApi";
import { CATEGORY_ORDER, CATEGORY_LABELS, STATUS_CONFIG, fmtMoney } from "@/b2b/constants";
import B2BLotEditor from "@/b2b/B2BLotEditor";

const ALLOWED = ["SUPER_ADMIN", "ADMIN"];

function LoginGate() {
  const { login } = useAuth();
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(id, pw); } catch { setErr("Invalid credentials"); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4" data-testid="b2b-admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl space-y-4">
        <div>
          <div className="text-lg font-extrabold">RENTOPC B2B — Admin</div>
          <div className="text-xs text-slate-500">Authorised staff only</div>
        </div>
        <input value={id} onChange={(e) => setId(e.target.value)} placeholder="Username or email" data-testid="admin-login-id"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="admin-login-pw"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
        {err && <div className="text-sm text-rose-600">{err}</div>}
        <button disabled={busy} data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} Sign in
        </button>
      </form>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function B2BAdminPage() {
  const { user, logout, loading: authLoading } = useAuth();
  const base = b2bBase();
  const [stats, setStats] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [editorId, setEditorId] = useState(undefined); // undefined=closed, null=new, id=edit
  const [settingsOpen, setSettingsOpen] = useState(false);

  const allowed = user && ALLOWED.includes(user.role);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([fetchAdminStats(), fetchAdminLots({ category: cat, q: q.trim() || undefined, include_archived: true })])
      .then(([s, l]) => { setStats(s); setLots(l); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cat, q]);

  useEffect(() => { if (allowed) reload(); }, [allowed, reload]);

  if (authLoading) return null;
  if (!user) return <LoginGate />;
  if (!allowed) return (
    <div className="min-h-screen bg-[#f5f7fa] flex flex-col items-center justify-center gap-3 text-slate-600" data-testid="b2b-admin-forbidden">
      <div>Your role ({user.role}) cannot manage the B2B catalogue.</div>
      <button onClick={logout} className="text-sm underline">Sign out</button>
    </div>
  );

  const onDuplicate = async (l) => {
    const n = window.prompt(`Duplicate lot ${l.lot_number} — enter a NEW lot number:`, `${l.lot_number}-COPY`);
    if (!n) return;
    try { await duplicateLot(l.id, n.trim()); reload(); }
    catch (e) { alert(e?.response?.data?.detail?.message || "Could not duplicate"); }
  };
  const onArchive = async (l) => { await archiveLot(l.id, !l.is_archived); reload(); };
  const onDelete = async (l) => {
    if (!window.confirm(`Permanently delete lot ${l.lot_number}? This removes the lot, its line items and B2B photos. This cannot be undone.`)) return;
    try { await deleteLot(l.id); reload(); }
    catch (e) { alert(e?.response?.data?.detail?.message || "Could not delete"); }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-[#17202a]" data-testid="b2b-admin">
      <header className="bg-[#111827] text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="text-lg font-extrabold">RENTOPC B2B — Admin</div>
          <div className="flex items-center gap-2">
            <a href={`${base}/`} target="_blank" rel="noreferrer" data-testid="admin-view-public"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">
              <ExternalLink size={14} /> Public
            </a>
            <button onClick={() => setSettingsOpen(true)} data-testid="admin-settings-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">
              <Settings size={14} /> Settings
            </button>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6 space-y-5">
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Total Lots" value={stats.total_lots} testid="stat-total-lots" />
            <StatCard label="Available Units" value={stats.total_available_units} testid="stat-units" />
            <StatCard label="Available" value={stats.available_lots} testid="stat-available" />
            <StatCard label="Low Stock" value={stats.low_stock_lots} testid="stat-low" />
            <StatCard label="Sold" value={stats.sold_lots} testid="stat-sold" />
            <StatCard label="On Hold" value={stats.on_hold_lots} testid="stat-hold" />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lot number or name…" data-testid="admin-search"
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="admin-category"
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
          </select>
          <button onClick={() => setEditorId(null)} data-testid="admin-add-lot"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700">
            <Plus size={15} /> Add Lot
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-lots-table">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Lot</th>
                  <th className="text-left px-3 py-2.5">Category</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-right px-3 py-2.5">Avail</th>
                  <th className="text-right px-3 py-2.5">From</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="py-16 text-center text-slate-400"><Loader2 className="inline animate-spin" size={18} /> Loading…</td></tr>
                ) : lots.length === 0 ? (
                  <tr><td colSpan={7} className="py-16 text-center text-slate-500" data-testid="admin-empty">No lots yet. Click “Add Lot”.</td></tr>
                ) : lots.map((l) => {
                  const cfg = STATUS_CONFIG[l.status] || STATUS_CONFIG.AVAILABLE;
                  return (
                    <tr key={l.id} className={`border-t border-slate-100 ${l.is_archived ? "opacity-60" : ""}`} data-testid={`admin-row-${l.lot_number}`}>
                      <td className="px-4 py-3 font-mono font-bold text-[13px]">{l.lot_number}{l.is_archived && <span className="ml-2 text-[10px] font-sans font-normal text-slate-400">(archived)</span>}</td>
                      <td className="px-3 py-3">{CATEGORY_LABELS[l.category] || l.category}</td>
                      <td className="px-3 py-3">{l.lot_name}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{l.total_available_quantity}/{l.total_original_quantity}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">{fmtMoney(l.price_from) || "—"}</td>
                      <td className="px-3 py-3"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${cfg.cls}`}>{cfg.label}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => setEditorId(l.id)} title="Edit" data-testid={`edit-${l.lot_number}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"><Pencil size={15} /></button>
                          <button onClick={() => onDuplicate(l)} title="Duplicate" data-testid={`duplicate-${l.lot_number}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"><Copy size={15} /></button>
                          <button onClick={() => onArchive(l)} title={l.is_archived ? "Unarchive" : "Archive"} data-testid={`archive-${l.lot_number}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600">{l.is_archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}</button>
                          <button onClick={() => onDelete(l)} title="Delete" data-testid={`delete-${l.lot_number}`} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {editorId !== undefined && (
        <B2BLotEditor lotId={editorId} onClose={() => setEditorId(undefined)}
          onSaved={() => { setEditorId(undefined); reload(); }} />
      )}
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function SettingsModal({ onClose }) {
  const [wa, setWa] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { fetchSettings().then((d) => setWa(d.whatsapp_number || "")).finally(() => setLoading(false)); }, []);
  const save = async () => {
    setSaving(true);
    try { await saveSettings({ whatsapp_number: wa.trim() || null }); onClose(); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4" data-testid="b2b-settings-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 space-y-4">
        <div className="font-bold text-slate-900">B2B Settings</div>
        {loading ? <div className="text-slate-400 text-sm">Loading…</div> : (
          <label className="block text-sm">Official WhatsApp Enquiry Number
            <input value={wa} onChange={(e) => setWa(e.target.value)} placeholder="e.g. 919811023300 (with country code)" data-testid="settings-whatsapp"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
            <span className="block text-[11px] text-slate-400 mt-1">Digits only, including country code. Leave blank to hide the WhatsApp button.</span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving || loading} data-testid="settings-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
            {saving && <Loader2 size={14} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
